import { useState } from 'react'
import { useAuthStore } from '../../stores'
import { stripeService } from '../../services/stripeService'
import { Button } from '../common'

/**
 * PaymentFailureBanner
 * ====================
 * Displays a persistent warning banner when user's subscription has a past_due status.
 * Provides a quick action button to open the Stripe Customer Portal for payment update.
 */
export function PaymentFailureBanner() {
  const { profile } = useAuthStore()
  const [dismissed, setDismissed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Only show if subscription is past_due and not dismissed
  if (profile?.subscription_status !== 'past_due' || dismissed) {
    return null
  }

  const handleUpdatePayment = async () => {
    if (!profile?.stripe_customer_id) return

    setIsLoading(true)
    try {
      const url = await stripeService.openCustomerPortal(profile.stripe_customer_id)
      if (url) {
        window.location.href = url
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-red-50 border-b border-red-200">
      <div className="px-4 py-3 max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Warning icon */}
            <svg
              className="w-5 h-5 text-red-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-red-800">
              <strong>Payment failed.</strong>{' '}
              <span className="hidden sm:inline">
                Please update your payment method to continue your subscription.
              </span>
              <span className="sm:hidden">Update payment method.</span>
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              onClick={handleUpdatePayment}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Update Payment'}
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
